import { Controller } from "@hotwired/stimulus"
import { stream } from "@durable-streams/client"
import { marked } from "marked"
import DOMPurify from "dompurify"

export default class extends Controller {
  static targets = ["generateAllButton"]
  static values = {
    activityId: String,
    inloopId: String
  }

  connect() {
    this.activeStreams = new Map()

    // Render existing content as markdown
    this.element.querySelectorAll("[data-output-content]").forEach(el => {
      if (el.textContent.trim()) {
        el.innerHTML = DOMPurify.sanitize(marked.parse(el.textContent.trim()))
      }
    })

    // Reconnect to any active streams (page refresh recovery)
    this.reconnectActiveStreams()
  }

  disconnect() {
    this.activeStreams.forEach(ac => ac.abort())
    this.activeStreams.clear()
  }

  // Reconnect to streams that were active before page refresh
  reconnectActiveStreams() {
    this.element.querySelectorAll("[data-output-type][data-stream-url]").forEach(card => {
      const streamUrl = card.dataset.streamUrl
      const outputType = card.dataset.outputType
      if (streamUrl) {
        this.connectToStream(outputType, streamUrl)
      }
    })
  }

  // Toggle card collapse/expand
  toggleCard(e) {
    if (e.target.closest("[data-generate-btn]")) return

    const outputType = e.currentTarget.dataset.outputType
    const card = this.element.querySelector(`[data-output-type="${outputType}"]`)
    if (!card) return

    const body = card.querySelector("[data-output-body]")
    const chevron = card.querySelector("[data-chevron]")
    if (!body) return

    const isHidden = body.classList.contains("hidden")
    body.classList.toggle("hidden")

    if (chevron) {
      const svg = chevron.querySelector("svg")
      if (svg) {
        svg.style.transform = isHidden ? "rotate(0deg)" : "rotate(180deg)"
      }
    }
  }

  // Generate a single output type
  async generate(e) {
    e.stopPropagation()
    const outputType = e.currentTarget.dataset.outputType
    await this.generateOutput(outputType, e.currentTarget)
  }

  // Called when user confirms reset inside a modal
  async resetOutputConfirmed(e) {
    e.stopPropagation()
    const outputType = e.currentTarget.dataset.outputType

    try {
      const response = await fetch(
        `/facilitator/inloops/${this.inloopIdValue}/activities/${this.activityIdValue}/reset_call_output`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({ output_type: outputType })
        }
      )

      if (response.ok) {
        this.clearCardContent(outputType)
      } else {
        console.error(`Failed to reset ${outputType}:`, await response.text())
      }
    } catch (error) {
      console.error(`Failed to reset ${outputType}:`, error)
    }
  }

  // Generate all outputs
  async generateAll() {
    if (this.hasGenerateAllButtonTarget) {
      this.generateAllButtonTarget.disabled = true
      this.generateAllButtonTarget.textContent = "Generating..."
    }

    try {
      const response = await fetch(
        `/facilitator/inloops/${this.inloopIdValue}/activities/${this.activityIdValue}/generate_call_output`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({ output_type: "all" })
        }
      )

      if (response.ok) {
        const data = await response.json()

        // Set all visible output cards to generating state
        this.element.querySelectorAll("[data-output-type]").forEach(card => {
          this.setCardStatus(card.dataset.outputType, "generating")
        })

        // Connect to durable streams (server determines which types based on call_type)
        if (data.stream_urls) {
          Object.entries(data.stream_urls).forEach(([type, url]) => {
            this.connectToStream(type, url)
          })
        }
      }
    } catch (error) {
      console.error("Failed to generate outputs:", error)
      if (this.hasGenerateAllButtonTarget) {
        this.generateAllButtonTarget.disabled = false
        this.generateAllButtonTarget.textContent = "Generate All"
      }
    }
  }

  async generateOutput(outputType, button) {
    if (button) {
      button.disabled = true
      button.textContent = "Generating..."
    }

    this.setCardStatus(outputType, "generating")

    try {
      const response = await fetch(
        `/facilitator/inloops/${this.inloopIdValue}/activities/${this.activityIdValue}/generate_call_output`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
          },
          body: JSON.stringify({ output_type: outputType })
        }
      )

      if (response.ok) {
        const data = await response.json()
        const streamUrl = data.stream_urls?.[outputType]
        if (streamUrl) {
          this.connectToStream(outputType, streamUrl)
        }
      } else {
        this.setCardStatus(outputType, "error")
        if (button) {
          button.disabled = false
          button.textContent = "Retry"
        }
      }
    } catch (error) {
      console.error(`Failed to generate ${outputType}:`, error)
      this.setCardStatus(outputType, "error")
      if (button) {
        button.disabled = false
        button.textContent = "Retry"
      }
    }
  }

  // Connect to a durable stream for real-time updates
  async connectToStream(outputType, streamUrl) {
    // Abort existing stream for this type if any
    if (this.activeStreams.has(outputType)) {
      this.activeStreams.get(outputType).abort()
    }

    const abortController = new AbortController()
    this.activeStreams.set(outputType, abortController)

    try {
      const res = await stream({
        url: streamUrl,
        offset: "-1",
        live: "sse",
        json: true,
        signal: abortController.signal
      })

      res.subscribeJson(async (batch) => {
        for (const item of batch.items) {
          this.handleStreamEvent(outputType, item)
        }
      })

      res.closed.then(() => {
        this.activeStreams.delete(outputType)
        this.checkAllComplete()
      })
    } catch (error) {
      if (error.name === "AbortError") return
      console.error(`[call-output] Stream error for ${outputType}:`, error)
      this.activeStreams.delete(outputType)
    }
  }

  handleStreamEvent(outputType, event) {
    switch (event.type) {
      case "completed":
        this.setCardContent(outputType, event.content)
        this.setCardStatus(outputType, "completed")
        break
      case "error":
        this.setCardStatus(outputType, "error")
        break
    }
  }

  checkAllComplete() {
    if (this.activeStreams.size === 0 && this.hasGenerateAllButtonTarget) {
      this.generateAllButtonTarget.disabled = false
      this.generateAllButtonTarget.textContent = "Generate All"
    }
  }

  setCardStatus(outputType, status) {
    const card = this.element.querySelector(`[data-output-type="${outputType}"]`)
    if (!card) return

    const generateBtn = card.querySelector("[data-generate-btn]")

    if (generateBtn) {
      if (status === "generating") {
        generateBtn.disabled = true
        generateBtn.innerHTML = `
          <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
          </svg>
          Generating...
        `
        generateBtn.classList.remove("hidden")
      } else if (status === "completed") {
        generateBtn.disabled = false
        generateBtn.textContent = "Regenerate"
        generateBtn.classList.remove("hidden")
      } else if (status === "error") {
        generateBtn.disabled = false
        generateBtn.textContent = "Retry"
        generateBtn.classList.remove("hidden")
      }
    }
  }

  setCardContent(outputType, content) {
    const card = this.element.querySelector(`[data-output-type="${outputType}"]`)
    if (!card) return

    let body = card.querySelector("[data-output-body]")

    // If no collapsible body exists yet (was pending), create it
    if (!body) {
      body = document.createElement("div")
      body.classList.add("border-t", "border-gray-100")
      body.setAttribute("data-output-body", "")
      body.innerHTML = `
        <div class="p-4">
          <div class="prose prose-sm max-w-none text-gray-700" data-output-content></div>
          <div class="flex items-center justify-between mt-4 pt-3 border-t border-gray-100" data-output-footer>
            <span class="text-xs text-gray-400">Just generated</span>
            <div class="flex items-center gap-2">
              <button type="button"
                      class="inline-flex items-center justify-center gap-2 rounded-lg px-3.5 py-2 text-text-sm font-semibold border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
                      data-generate-btn
                      data-output-type="${outputType}"
                      data-action="click->call-output#generate">
                Regenerate
              </button>
              <div data-controller="modal" data-reset-btn>
                <button type="button"
                        data-action="click->modal#open"
                        class="inline-flex items-center justify-center gap-2 rounded-lg px-3.5 py-2 text-text-sm font-semibold bg-transparent text-[#B42318] hover:bg-error-50 hover:text-[#912018] transition-colors">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                  Reset
                </button>
                <div data-modal-target="backdrop" data-action="click->modal#close" class="fixed inset-0 z-20 bg-alpha-black-40 backdrop-blur-sm hidden opacity-0 transition-opacity duration-150 ease-out"></div>
                <div data-modal-target="container" class="fixed inset-0 z-50 flex items-center justify-center p-4 hidden">
                  <div data-modal-target="dialog" data-action="click->modal#stopPropagation" class="bg-white rounded-xl w-full max-w-[400px] shadow-[0px_20px_24px_-4px_rgba(16,24,40,0.08),0px_8px_8px_-4px_rgba(16,24,40,0.03)] relative flex flex-col max-h-[90vh] overflow-hidden opacity-0 scale-95 transition-all duration-150 ease-out" role="alertdialog" aria-modal="true">
                    <button type="button" class="absolute z-10 top-4 right-4 p-2 rounded-lg w-11 h-11 flex items-center justify-center hover:bg-gray-100 transition-colors focus-visible:outline-none" data-action="click->modal#close" aria-label="Close modal">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-gray-400"><path d="M18 6L6 18M6 6l12 12"/></svg>
                    </button>
                    <div class="pt-6 px-6 md:pt-6 md:px-6">
                      <div class="flex flex-col gap-2 pr-8">
                        <div class="w-12 h-12 rounded-full bg-error-100 flex items-center justify-center">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#F04438" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                        </div>
                        <h3 class="text-text-lg font-semibold text-gray-900">Reset ${outputType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</h3>
                        <p class="text-text-sm text-gray-600">Are you sure you want to reset this output? This will clear the generated content and you will need to generate it again.</p>
                      </div>
                    </div>
                    <div class="px-4 py-4 md:px-6 md:py-5 flex-1 overflow-y-auto">
                      <div class="flex gap-3 justify-end">
                        <button type="button" class="inline-flex items-center justify-center gap-2 rounded-lg px-3.5 py-2 text-text-sm font-semibold border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 transition-colors" data-action="click->modal#close">Cancel</button>
                        <button type="button" class="inline-flex items-center justify-center gap-2 rounded-lg px-3.5 py-2 text-text-sm font-semibold bg-error-600 text-white hover:bg-error-700 transition-colors" data-action="click->modal#close click->call-output#resetOutputConfirmed" data-output-type="${outputType}">Reset</button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      `
      card.appendChild(body)

      // Remove inline generate button from header
      const headerBtns = card.querySelector("[data-generate-btn]")
      if (headerBtns && !headerBtns.closest("[data-output-body]")) {
        headerBtns.remove()
      }

      // Add chevron to header
      const actionsArea = card.querySelector(".flex.items-center.gap-2.flex-shrink-0")
      if (actionsArea && !actionsArea.querySelector("[data-chevron]")) {
        const chevronDiv = document.createElement("div")
        chevronDiv.classList.add("transition-transform", "duration-200")
        chevronDiv.setAttribute("data-chevron", "")
        chevronDiv.innerHTML = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-5 h-5 text-gray-400"><path d="M18 15l-6-6-6 6"/></svg>`
        actionsArea.appendChild(chevronDiv)
      }

      // Make header clickable
      const header = card.firstElementChild
      if (header) {
        header.classList.add("cursor-pointer", "hover:bg-gray-50", "transition-colors")
        header.setAttribute("data-action", "click->call-output#toggleCard")
        header.setAttribute("data-output-type", outputType)
      }
    }

    const contentArea = body.querySelector("[data-output-content]")
    if (contentArea) {
      contentArea.innerHTML = DOMPurify.sanitize(marked.parse(content))
    }

    body.classList.remove("hidden")

    // Update timestamp
    const footer = body.querySelector("[data-output-footer]")
    if (footer) {
      const timeSpan = footer.querySelector("span")
      if (timeSpan) timeSpan.textContent = "Just generated"
    }

    // Clean up stream URL attribute (no longer needed)
    card.removeAttribute("data-stream-url")
  }

  clearCardContent(outputType) {
    const card = this.element.querySelector(`[data-output-type="${outputType}"]`)
    if (!card) return

    // Remove collapsible body
    const body = card.querySelector("[data-output-body]")
    if (body) body.remove()

    // Remove chevron
    const chevron = card.querySelector("[data-chevron]")
    if (chevron) chevron.remove()

    // Reset header to pending state
    const header = card.firstElementChild
    if (header) {
      header.classList.remove("cursor-pointer", "hover:bg-gray-50", "transition-colors")
      header.removeAttribute("data-action")
      header.removeAttribute("data-output-type")
    }

    // Remove any leftover reset button/modal from header
    const headerResetBtn = card.querySelector("[data-reset-btn]")
    if (headerResetBtn && !headerResetBtn.closest("[data-output-body]")) {
      headerResetBtn.remove()
    }

    // Restore Generate button in header actions area
    const actionsArea = card.querySelector(".flex.items-center.gap-2.flex-shrink-0")
    if (actionsArea && !actionsArea.querySelector("[data-generate-btn]")) {
      const generateBtn = document.createElement("button")
      generateBtn.type = "button"
      generateBtn.className = "inline-flex items-center justify-center gap-2 rounded-lg px-3.5 py-2 text-text-sm font-semibold border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
      generateBtn.setAttribute("data-generate-btn", "")
      generateBtn.setAttribute("data-output-type", outputType)
      generateBtn.setAttribute("data-action", "click->call-output#generate")
      generateBtn.textContent = "Generate"
      actionsArea.appendChild(generateBtn)
    }

    // Clean up any remaining stream URL
    card.removeAttribute("data-stream-url")
  }

}
